/** Role-aware first-session walkthrough + Help & Support → Feature Guide (replay). */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import useAuthStore from '../lib/auth-store';

const GUIDE = [
  { key: 'crm', title: 'CRM', text: 'Your day: Dashboard, My Day, leads, prospecting, merchants, applications and My Residuals.', href: '/', roles: 'all' },
  { key: 'pipelines', title: 'Pipelines', text: 'Track Merchant Services, POS and Lending opportunities from first touch to win.', href: '/pipeline/merchant_services', roles: 'all' },
  { key: 'tools', title: 'Commission Calculator & E-Learning', text: 'Quote your own deals and keep training up to date.', href: '/residuals/calculator', roles: 'all' },
  { key: 'team', title: 'Team', text: 'Invite and manage your downline — Schedule A, split, bonuses and defaults for people below you.', href: '/users', roles: 'builder' },
  { key: 'finance', title: 'Residuals & Finance', text: 'Payout runs, schedule approvals, reconciliation and payee banking.', href: '/payout-run', roles: 'manager' },
  { key: 'platform', title: 'Platform (MSC)', text: 'Tenants, enrollment requests, MSC-MBI provisioning, cost basis and audit.', href: '/admin', roles: 'super_admin' },
  { key: 'account', title: 'Account', text: 'My Settings, Help & Support and this Feature Guide — replay the tour any time.', href: '/my-settings', roles: 'all' },
];

export function visibleGuide(role, canBuild) {
  return GUIDE.filter((g) => g.roles === 'all' || (g.roles === 'builder' && canBuild) || (g.roles === 'manager' && ['iso', 'super_admin'].includes(role)) || g.roles === role);
}

export function Walkthrough({ force, onClose }) {
  const user = useAuthStore((s) => s.user);
  const [state, setState] = useState(null);
  const [step, setStep] = useState(0);
  const [header, setHeader] = useState(null);
  useEffect(() => {
    api.request('/me/walkthrough').then(setState).catch(() => {});
    api.request('/me/profile-header').then(setHeader).catch(() => {});
  }, []);
  const items = visibleGuide(user?.role, header?.can_build_downline);
  const open = force || state?.should_start;
  if (!open || !items.length) return null;
  const finish = (event) => { api.request('/me/walkthrough', { method: 'POST', body: JSON.stringify({ event, step }) }).catch(() => {}); setState({ should_start: false }); onClose?.(); };
  const it = items[step];
  return (
    <div className="fixed inset-0 z-[90] flex items-end justify-center bg-black/30 p-4 sm:items-center" data-testid="walkthrough">
      <div className="w-full max-w-md rounded-2xl bg-card p-6 shadow-xl">
        <p className="text-xs uppercase tracking-wide text-muted-foreground">Step {step + 1} of {items.length}</p>
        <h2 className="mt-1 text-lg font-semibold" data-testid="walkthrough-title">{it.title}</h2>
        <p className="mt-2 text-sm text-muted-foreground">{it.text}</p>
        <div className="mt-5 flex justify-between gap-2">
          <button type="button" className="text-sm text-muted-foreground hover:underline" onClick={() => finish('dismissed')} data-testid="walkthrough-skip">Skip tour</button>
          {step < items.length - 1
            ? <button type="button" className="h-9 rounded-full bg-primary px-4 text-sm text-primary-foreground" onClick={() => setStep(step + 1)} data-testid="walkthrough-next">Next</button>
            : <button type="button" className="h-9 rounded-full bg-primary px-4 text-sm text-primary-foreground" onClick={() => finish('completed')} data-testid="walkthrough-done">Finish</button>}
        </div>
      </div>
    </div>
  );
}

export default function FeatureGuidePage() {
  const role = useAuthStore((s) => s.user?.role);
  const [header, setHeader] = useState(null);
  const [replay, setReplay] = useState(false);
  useEffect(() => { api.request('/me/profile-header').then(setHeader).catch(() => {}); }, []);
  return (
    <div className="space-y-6" data-testid="feature-guide-page">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-2xl font-bold">Feature Guide</h1>
        <button type="button" className="h-9 rounded-full border px-4 text-sm" onClick={() => { api.request('/me/walkthrough', { method: 'POST', body: JSON.stringify({ event: 'replay' }) }).catch(() => {}); setReplay(true); }} data-testid="replay-walkthrough-btn">Replay Walkthrough</button>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        {visibleGuide(role, header?.can_build_downline).map((g) => (
          <div key={g.key} className="rounded-xl border bg-card p-5" data-testid={`guide-${g.key}`}>
            <h2 className="font-semibold">{g.title}</h2><p className="mt-1 text-sm text-muted-foreground">{g.text}</p>
            <Link to={g.href} className="mt-3 inline-block text-sm font-medium underline" data-testid={`guide-learn-${g.key}`}>Learn More</Link>
          </div>
        ))}
      </div>
      {replay && <Walkthrough force onClose={() => setReplay(false)} />}
    </div>
  );
}
